"use client";

import { Check, Minus } from "@icon-park/svg";
import clsx from "clsx";
import {
  ChangeEvent,
  FC,
  forwardRef,
  InputHTMLAttributes,
  PropsWithChildren,
  useRef,
} from "react";
import ErrorMessage from "../ErrorMessage";
import Icon from "../Icon";

type CheckboxSize = "sm" | "md";

export interface CheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  checked?: boolean;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  value?: string | number;
  defaultChecked?: boolean;
  inverted?: boolean;
  onBlur?: (event: ChangeEvent<HTMLInputElement>) => void;
  name?: string;
  size?: CheckboxSize;
  errorMessage?: string;
  className?: string;
  disabled?: boolean;
  labelLeft?: boolean;
  variant?: "primary" | "marigold" | "mindaro" | "cadet";
  indeterminate?: boolean;
}

const sizeClasses = {
  md: {
    common: "w-3s h-3s",
    label: "text-body-2",
  },
  sm: {
    common: "w-2s h-2s",
    label: "text-app-body-2",
  },
};

const variantClasses = {
  primary:
    "group-hover:border-primary-825 group-active:border-primary-850 ring-primary-800 peer-checked:bg-primary-800 peer-checked:border-primary-800 group-hover:peer-checked:bg-primary-825 group-hover:peer-checked:border-primary-825 group-active:peer-checked:bg-primary-850 group-active:peer-checked:border-primary-850",
  mindaro:
    "group-hover:border-mindaro-850 group-active:border-mindaro-850 ring-mindaro-850 peer-checked:bg-mindaro-850 peer-checked:border-mindaro-850 group-hover:peer-checked:bg-mindaro-850 group-hover:peer-checked:border-mindaro-850 group-active:peer-checked:bg-mindaro-850 group-active:peer-checked:border-mindaro-850",
  marigold:
    "group-hover:border-marigold-750 group-active:border-marigold-750 ring-marigold-750 peer-checked:bg-marigold-750 peer-checked:border-marigold-750 group-hover:peer-checked:bg-marigold-750 group-hover:peer-checked:border-marigold-750 group-active:peer-checked:bg-marigold-750 group-active:peer-checked:border-marigold-750",
  cadet:
    "group-hover:border-cadet-850 group-active:border-cadet-900 ring-cadet-850 peer-checked:bg-cadet-850 peer-checked:border-cadet-850 group-hover:peer-checked:bg-cadet-850 group-hover:peer-checked:border-cadet-850 group-active:peer-checked:bg-cadet-900 group-active:peer-checked:border-cadet-900",
};

const commonClasses =
  "flex-none border rounded-xs flex justify-center items-center peer-focus-visible:ring-2";

const classes =
  "bg-neutral-0 border-neutral-150 ring-offset-2 text-neutral-0 peer-disabled:border-neutral-50 peer-checked:peer-disabled:bg-neutral-50 group-hover:peer-checked:peer-disabled:bg-neutral-50 group-hover:peer-checked:peer-disabled:border-neutral-50";

const classesInverted =
  "text-transparent bg-transparent border-neutral-0 group-hover:border-neutral-100 group-active:border-neutral-150 ring-neutral-0 peer-checked:text-primary-800 peer-checked:bg-neutral-0 peer-checked:border-neutral-0 group-hover:peer-checked:bg-neutral-25 group-hover:peer-checked:border-neutral-25 group-active:peer-checked:bg-neutral-50 group-active:peer-checked:border-neutral-50 text-primary-800";

const Checkbox: FC<PropsWithChildren<CheckboxProps>> = forwardRef<
  HTMLInputElement,
  PropsWithChildren<CheckboxProps>
>(function Checkbox(
  {
    children,
    value,
    defaultChecked,
    inverted = false,
    onBlur,
    onChange,
    name,
    size,
    errorMessage,
    checked,
    className,
    disabled,
    labelLeft,
    variant = "primary",
    indeterminate,
  },
  ref,
) {
  size = size || "md";
  const checkboxRef = useRef<HTMLInputElement | null>();

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (typeof onChange === "function") {
      onChange(event);
    }
  };

  return (
    <div className={className}>
      <label
        data-testid={name}
        className={clsx("group cursor-pointer inline-flex gap-2xs", {
          "justify-between w-full flex-row-reverse": labelLeft,
        })}
      >
        <input
          ref={(el) => {
            checkboxRef.current = el;
            if (typeof ref === "function") {
              ref(el);
            }
          }}
          disabled={disabled}
          type="checkbox"
          className="peer w-none h-none absolute opacity-0"
          onChange={handleChange}
          onBlur={onBlur}
          value={value}
          name={name}
          checked={checked}
          defaultChecked={defaultChecked}
        />
        <div
          className={clsx(sizeClasses[size].common, commonClasses, {
            [classesInverted]: inverted,
            [classes]: !inverted,
            [variantClasses[variant]]: !inverted,
          })}
        >
          <Icon
            icon={indeterminate ? Minus : Check}
            size={size === "md" ? 16 : 14}
          />
        </div>
        <span className={sizeClasses[size].label}>{children}</span>
      </label>
      <div>{errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}</div>
    </div>
  );
});

export default Checkbox;
